import { Constructor, Nullable, UnderlyingType } from "../Extensions";
import { BuiltInTypes } from "../Server/DotNet/System/BuiltInTypes";
import { ArrayHelper } from "./ArrayHelper";
import { DateHelper } from "./DateHelper";
import { MathHelper } from "./MathHelper";
import { StringHelper } from "./StringHelper";
import { NAMESPACE } from "./_Namespace";

export class ObjectHelper
{
    public static classNamespace = NAMESPACE + "ObjectHelper";
    public static className = "ObjectHelper";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Public Methods

    public static asDotNetType(value: any, type: BuiltInTypes)
    {
        const
            nullableMatches = /System\.Nullable`1\[\[(.*)?\]\], System.Private.CoreLib/gi.exec(type);

        let
            isNullable = false;

        if (nullableMatches)
        {
            isNullable = true;
            type = nullableMatches[1] as BuiltInTypes;
        }

        if (value === undefined || value === null)
        {
            if (type === "System.Object, System.Private.CoreLib" || type === "System.String, System.Private.CoreLib" || isNullable)
                return null;

            throw new Error(`${type} does not support null`);
        }

        if (type === "System.Object, System.Private.CoreLib")
            return value;

        if (type === "System.Object[], System.Private.CoreLib")
            return value;

        const
            sourceType = this.typeOf(value),
            stringValue: string = value.toString();

        let
            numberValue = parseFloat(stringValue);

        switch (type)
        {
            case "System.Boolean, System.Private.CoreLib":
                {
                    if (stringValue.toLowerCase() === "true" || stringValue === "1")
                        return true;

                    if (stringValue.toLowerCase() === "false" || stringValue === "0")
                        return false;

                    return null;
                }
            case "System.Byte, System.Private.CoreLib":
            case "System.SByte, System.Private.CoreLib":
                {
                    if (!isNaN(numberValue))
                    {
                        numberValue = Math.round(numberValue);

                        switch (type)
                        {
                            case "System.SByte, System.Private.CoreLib":
                                return MathHelper.clamp(numberValue, -128, 127);
                            default:
                                return MathHelper.clamp(numberValue, 0, 255);
                        }
                    }

                    return null;
                }
            case "System.Char, System.Private.CoreLib":
                {
                    if (!StringHelper.isNullOrWhitespace(stringValue) && stringValue.length)
                        return stringValue[0];

                    return null;
                }
            case "System.DateOnly, System.Private.CoreLib":
                {
                    switch (sourceType)
                    {
                        case "date":
                            return DateHelper.toString(value, "yyyy-MM-dd");
                        case "string":
                            if (StringHelper.isDate(value, "yyyy-MM-dd"))
                                return value;
                    }
                    break;
                }
            case "System.DateTime, System.Private.CoreLib":
                {
                    switch (sourceType)
                    {
                        case "date":
                            return DateHelper.toString(value, "yyyy-MM-ddTHH:mm:ss.fffffffZ");
                        case "string":
                            if (StringHelper.isDate(value, "yyyy-MM-ddTHH:mm:ss.fffffffZ"))
                                return value;
                    }
                    break;
                }
            case "System.Decimal, System.Private.CoreLib":
            case "System.Double, System.Private.CoreLib":
            case "System.Single, System.Private.CoreLib":
                {
                    if (!isNaN(numberValue))
                        return numberValue;

                    return null;
                }
            case "System.Guid, System.Private.CoreLib":
                {
                    if (StringHelper.isGuid(stringValue))
                        return stringValue;

                    return null;
                }
            case "System.Int16, System.Private.CoreLib":
            case "System.Int32, System.Private.CoreLib":
            case "System.Int64, System.Private.CoreLib":
            case "System.UInt16, System.Private.CoreLib":
            case "System.UInt32, System.Private.CoreLib":
            case "System.UInt64, System.Private.CoreLib":
                {
                    if (!isNaN(numberValue))
                    {
                        numberValue = Math.round(numberValue);

                        switch (type)
                        {
                            case "System.UInt16, System.Private.CoreLib":
                            case "System.UInt32, System.Private.CoreLib":
                            case "System.UInt64, System.Private.CoreLib":
                                return Math.abs(numberValue);
                            default:
                                return numberValue;
                        }
                    }

                    return null;
                }
            case "System.String, System.Private.CoreLib":
                {
                    if (!StringHelper.isNullOrWhitespace(stringValue))
                        return stringValue;

                    return null;
                }
            default:
                return value;
            //throw new Error(StringHelper.format("Type '{0}' is unsupported", type));
        }
        //throw new Error(StringHelper.format("Failed to convert value of type '{0}' to type '{0}'", sourceType, type));
    }
    public static asType(value: any, type: UnderlyingType)
    {
        if (type === "object")
            return value;

        const
            sourceType = this.typeOf(value);

        if (sourceType === type || value === undefined || value === null)
            return value;

        const
            stringValue = value.toString().toLowerCase();

        let
            numberValue;

        switch (type)
        {
            case "array":
                {
                    if (sourceType === "string")
                    {
                        let
                            array;

                        try { array = ObjectHelper.fromJson(value); }
                        catch (e) { /* Continue regardless of error */ }

                        if (array)
                            return array;
                    }

                    break;
                }
            case "boolean":
                {
                    if (stringValue === "true" || stringValue === "1")
                        return true;

                    if (stringValue === "false" || stringValue === "0")
                        return false;

                    return null;
                }
            case "date":
                {
                    let
                        year,
                        month,
                        day,
                        hour,
                        minute,
                        second,
                        date;

                    // TODO: User String -> Date Functions
                    if (stringValue.length === 10)
                    {
                        year = parseInt(stringValue.substr(0, 4));
                        month = parseInt(stringValue.substr(5, 2));
                        day = parseInt(stringValue.substr(8, 2));

                        if (isNaN(year) || isNaN(month) || isNaN(day))
                            return null;

                        date = new Date(year, month - 1, day);

                        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
                            return null;

                        return date;
                    }
                    else if (stringValue.length === 24)
                    {
                        year = parseInt(stringValue.substr(0, 4));
                        month = parseInt(stringValue.substr(5, 2));
                        day = parseInt(stringValue.substr(8, 2));
                        hour = parseInt(stringValue.substr(11, 2));
                        minute = parseInt(stringValue.substr(14, 2));
                        second = parseInt(stringValue.substr(17, 2));

                        if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute) || isNaN(second))
                            return null;

                        date = new Date(year, month - 1, day, hour, minute, second);

                        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day || date.getHours() !== hour || date.getMinutes() !== minute || date.getSeconds() !== second)
                            return null;

                        return date;
                    }
                    return null;
                }
            case "datestring":
                {
                    switch (sourceType)
                    {
                        case "date":
                            return DateHelper.toString(value, "yyyy-MM-dd");
                        case "string":
                            let
                                dateTryFromString = DateHelper.tryFromString(value, "yyyy-MM-ddTHH:mm:ss.fffffffZ");

                            if (dateTryFromString.success)
                                return DateHelper.toString(dateTryFromString.date, "yyyy-MM-dd");

                            dateTryFromString = DateHelper.tryFromString(value, "yyyy-MM-dd");

                            if (dateTryFromString.success)
                                return DateHelper.toString(dateTryFromString.date, "yyyy-MM-dd");
                    }
                    break;
                }
            case "datetimestring":
                {
                    switch (sourceType)
                    {
                        case "date":
                            return DateHelper.toString(value, "yyyy-MM-ddTHH:mm:ss.fffffffZ");
                        case "string":
                            if (StringHelper.isDate(value, "yyyy-MM-ddTHH:mm:ss.fffffffZ"))
                                return value;
                    }
                    break;
                }
            case "float":
                {
                    numberValue = parseFloat(stringValue);

                    if (!isNaN(numberValue))
                        return numberValue;

                    return null;
                }
            case "int":
                {
                    numberValue = parseInt(stringValue);

                    if (!isNaN(numberValue))
                        return numberValue;

                    return null;
                }
            case "string":
                {
                    if (!StringHelper.isNullOrWhitespace(stringValue))
                        return stringValue;

                    return null;
                }
            default:
                throw new Error(StringHelper.format("Type '{0}' is unsupported", type));
        }

        throw new Error(StringHelper.format("Failed to convert value of type '{0}' to type '{1}'", sourceType, type));
    }
    public static classInheritsClass(constructor: Constructor, superClassConstructor: any): boolean
    {
        if (!constructor)
            return false;

        let
            currentConstructor = constructor;

        while (currentConstructor)
        {
            if (currentConstructor === superClassConstructor)
                return true;

            currentConstructor = Object.getPrototypeOf(currentConstructor);
        }

        return false;
    }
    public static classInheritsClassName<T>(object: any, classFullName: string): object is T
    {
        if (!object)
            return false;

        let
            currentPrototype = Object.getPrototypeOf(object);

        while (currentPrototype)
        {
            if (currentPrototype.constructor.classFullName === classFullName)
                return true;

            currentPrototype = Object.getPrototypeOf(currentPrototype);
        }

        return false;
    }
    public static cloneObject<T>(obj: T): T
    {
        if (obj === undefined)
            return obj;

        if (obj === null)
            return obj;

        return ObjectHelper.fromJson(this.toJson(obj));
    }
    public static combine(...args: any[]): any
    {
        // TODO: Copy isn't deep. Child object references are copied, not cloned :S
        const
            returnValue: any = {};

        for (let i = 0; i < args.length; i++)
        {
            for (let property in args[i])
            {
                if (Object.prototype.hasOwnProperty.call(args[i], property))
                    returnValue[property] = args[i][property];
            }
        }

        return returnValue;
    }
    public static compare(o: any, value: any): -1 | 0 | 1
    {
        if (o === undefined)
            o = null;

        if (value === undefined)
            value = null;

        if (o === null || value === null)
        {
            if (o === null)
            {
                if (value !== null)
                    return -1;
                else
                    return 0;
            }
            else
                return 1;
        }

        if (typeof o !== typeof value)
            return -1;

        if (o < value)
            return -1;

        if (o > value)
            return 1;

        return 0;
    }
    public static copySimpleProperties(source: any, destination: any, predicate?: (property: string) => boolean)
    {
        for (let property in source)
        {
            if (!this.isNullOrUndefined(source[property]) && this.typeOf(source[property]) === "object")
                continue;

            if (!predicate || predicate(property))
                destination[property] = source[property];
        }
    }
    public static extend(target: any, object1: any, ...objectN: any[]): any
    public static extend(deep: boolean, target: any, object1?: any, ...objectN: any[]): any
    public static extend(deepOrTarget: any, targetOrObject1: any, ...object1OrObjects: any[]): any
    {
        let
            deep = false,
            target,
            object1,
            objectN;

        if (typeof deepOrTarget === "boolean")
        {
            deep = deepOrTarget;
            target = targetOrObject1;
            objectN = object1OrObjects;
        }
        else
        {
            target = deepOrTarget;
            object1 = targetOrObject1;
            objectN = object1OrObjects;
        }

        const
            objects = object1 ? [object1] : [];

        if (objectN)
            objects.push(...objectN);

        for (let i = 0; i < objects.length; i++)
        {
            const
                source = objects[i];

            if (source === null || source === undefined)
                continue;

            if (ArrayHelper.isArrayLike(source))
            {
                if (!ArrayHelper.isArrayLike(target))
                    throw new Error("Source was an array but the target was not");

                for (let j = 0; j < source.length; j++)
                {
                    if (ArrayHelper.isArrayLike(source[j]))
                    {
                        if (!ArrayHelper.isArrayLike(target[j]))
                            target[j] = [];

                        this.extend(deep, target[j], source[j]);
                    }
                    else if (ObjectHelper.isPlainObject(source[j]))
                    {
                        if (!this.isPlainObject(target[j]))
                            target[j] = {};

                        this.extend(deep, target[j], source[j]);
                    }
                    else if (source[j] !== undefined)
                        target[j] = source[j];
                }
            }

            for (let property in source)
            {
                if (property === "__proto__")
                    continue;

                if (target === source[property])
                    continue;

                if (ArrayHelper.isArrayLike(source[property]))
                {
                    if (!ArrayHelper.isArrayLike(target[property]))
                        target[property] = [];

                    this.extend(deep, target[property], source[property]);
                }
                else if (this.isPlainObject(source[property]))
                {
                    if (!this.isPlainObject(target[property]))
                        target[property] = {};

                    if (deep)
                        this.extend(deep, target[property], source[property]);
                    else
                        target[property] = source[property];
                }
                else if (typeof source[property] === "function")
                {
                    if (Object.prototype.hasOwnProperty.call(source, property))
                        target[property] = source[property];
                }
                else if (source[property] !== undefined)
                    target[property] = source[property];
            }
        }

        return target;
    }
    public static fromJson(s: string, reviver?: (this: any, key: string, value: any) => any): any
    {
        return JSON.parse(s, reviver);
    }
    public static getClass(obj: any)
    {
        return obj.constructor ?? obj;
    }
    public static getClassHierarchy(obj: any): string[]
    {
        const
            classHierarchy: string[] = [];

        let
            currentPrototype = Object.getPrototypeOf(obj);

        while (currentPrototype)
        {
            classHierarchy.push(currentPrototype.constructor.classFullName ?? currentPrototype.constructor.name);
            currentPrototype = Object.getPrototypeOf(currentPrototype);
        }

        return classHierarchy;
    }
    public static getPropertyNames(obj: any)
    {
        const
            result = new Set<string>();

        while (obj)
        {
            Object.getOwnPropertyNames(obj).forEach(item => result.add(item));
            obj = Object.getPrototypeOf(obj);
        }
        return [...result];
    }
    public static hasProperty(obj: any, propertyName: string)
    {
        return this.getPropertyNames(obj).indexOf(propertyName) >= 0;
    }
    public static isAssignableTo<T>(obj: any, classFullName: string): obj is T
    public static isAssignableTo<T>(obj: any, classInfo: { fullName: string, interfaceType?: T }): obj is T
    public static isAssignableTo<T>(obj: any, classFullNameOrClassInfo: string | { fullName: string, interfaceType?: T }): obj is T
    public static isAssignableTo<T>(obj: any, classFullNameOrClassInfo: string | { fullName: string, interfaceType?: T }): obj is T
    {
        if (!obj)
            return false;

        const
            classFullName = typeof classFullNameOrClassInfo === "string" ? classFullNameOrClassInfo : classFullNameOrClassInfo.fullName;

        let
            currentPrototype = Object.getPrototypeOf(obj);

        while (currentPrototype)
        {
            if (classFullName === currentPrototype.constructor.classFullName)
                return true;

            currentPrototype = Object.getPrototypeOf(currentPrototype);
        }

        return false;
    }
    public static isEmpty(value: any)
    {
        if (typeof value === "string")
            return StringHelper.isNullOrWhitespace(value);

        return this.isNullOrUndefined(value);
    }
    public static isNullOrUndefined(value: any): value is null | undefined
    {
        return value === undefined || value === null;
    }
    public static isPlainObject(obj: any)
    {
        return (
            obj !== null
            && typeof obj === "object"
            && obj.constructor === Object
            && Object.prototype.toString.call(obj) === "[object Object]"
        );
    }
    public static propertyValueByPathGet<T extends bigint | boolean | Function | null | number | object | string | symbol | undefined = any>(o: any, path: string): T | undefined
    {
        if (!o)
            return undefined;

        const
            pathParts = path.split(".");

        let
            obj = o;

        for (let i = 0; i < pathParts.length; i++)
        {
            if (obj && obj[pathParts[i]] !== undefined)
                obj = obj[pathParts[i]];
            else
            {
                obj = undefined;
                break;
            }
        }

        return obj;
    }
    public static propertyValueByPathSet<T extends bigint | boolean | Function | null | number | object | string | symbol | undefined = any>(o: any, path: string, value: T)
    {
        const
            pathParts = path.split(".");

        let
            obj = o,
            changed = false;

        for (let i = 0; i < pathParts.length; i++)
        {
            if (i === pathParts.length - 1)
            {
                if (obj[pathParts[i]] !== value)
                {
                    obj[pathParts[i]] = value;
                    changed = true;
                }
                break;
            }
            else
            {
                if (obj[pathParts[i]] === undefined || obj[pathParts[i]] === null)
                    obj[pathParts[i]] = {};

                obj = obj[pathParts[i]];
            }
        }
        return changed;
    }
    public static toJson(obj: any, replacer?: (this: any, key: string, value: any) => any, space?: string | number): string
    public static toJson(obj: any, replacer?: Nullable<(number | string)[]>, space?: string | number): string
    public static toJson(obj: any, replacer?: any, space?: string | number): string
    {
        return JSON.stringify(obj, replacer, space);
    }
    public static toKeyValueArray(obj: any)
    {
        return Object.keys(obj).map(key => ({ key: key, value: obj[key] }));
    }
    public static typeOf(value: any): "bigint" | "boolean" | "function" | "number" | "object" | "string" | "symbol" | "undefined" | "date" | "bigint[]" | "boolean[]" | "function[]" | "number[]" | "object[]" | "string[]" | "symbol[]" | "date[]"
    {
        let
            type = typeof value;

        if (value instanceof Date)
            return "date";

        if (type === "object")
        {
            if (ArrayHelper.isArrayLike(value))
            {
                if (value.length)
                {
                    const
                        itemType = this.typeOf(value[0]);

                    switch (itemType)
                    {
                        case "bigint": return "bigint[]";
                        case "boolean": return "boolean[]";
                        case "date": return "date[]";
                        case "function": return "function[]";
                        case "number": return "number[]";
                        case "string": return "string[]";
                        case "symbol": return "symbol[]";
                        default: return "object[]";
                    }
                }

                return "object[]";
            }
        }

        return type;
    }
    public static valueEquals(obj1: any, obj2: any)
    {
        if (obj1 === obj2)
            return true;

        if (!(obj1 instanceof Object) || !(obj2 instanceof Object))
            return false;

        if (obj1.constructor !== obj2.constructor)
            return false;

        for (let property in obj1)
        {
            if (!Object.prototype.hasOwnProperty.call(obj1, property))
                continue;

            if (!Object.prototype.hasOwnProperty.call(obj2, property))
                return false;

            if (obj1[property] === obj2[property])
                continue;

            if (typeof obj1[property] !== "object")
                return false;

            if (!this.valueEquals(obj1[property], obj2[property]))
                return false;
        }

        for (let property in obj2)
        {
            if (Object.prototype.hasOwnProperty.call(obj2, property) && !Object.prototype.hasOwnProperty.call(obj1, property))
                return false;
        }

        return true;
    }

    // #endregion
}